import "./SwipeableModals.scss";
import React, { useState, useEffect } from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import {
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import AddPhoto from "./../../../assets/add-photo.png";
import BlueButton from "../../../components/BlueButton/BlueButton";
import api from "../../../utils/api";
import { photosApi } from "../../../SourceData";

import { toast } from "react-toastify";

const SwipeableModals = ({
  open,
  onClose,
  onOpen,
  currentView,
  editData,
  onSubmitSuccess,
  categories,
}) => {
  // Form state
  const [department, setDepartment] = useState("");
  const [services, setServices] = useState([
    { nameAr: "", nameEn: "", price: "", descriptionAr: "", descriptionEn: "" },
  ]);
  const [loading, setLoading] = useState(false);
  
  // Image state
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const [originalImageUrl, setOriginalImageUrl] = useState(null); // Track original image
  const [photoRemoved, setPhotoRemoved] = useState(false); // Track if user removed photo

  // Reset form when modal opens or edit data changes
  useEffect(() => {
    if (editData && currentView === "edit") {
      setDepartment(editData.category._id || "");

      // For edit mode, we're dealing with a single service
      setServices([
        {
          nameAr: editData.name?.ar || "",
          nameEn: editData.name?.en || "",
          price: editData.price?.toString() || "",
          descriptionAr: editData.description?.ar || "",
          descriptionEn: editData.description?.en || "",
        },
      ]);

      // Set image if exists
      if (editData.photo) {
        const imageUrl = photosApi + editData.photo;
        setImage(imageUrl);
        setOriginalImageUrl(imageUrl);
      } else {
        setImage(null);
        setOriginalImageUrl(null);
      }
      setPhotoRemoved(false);
    } else if (currentView === "create") {
      // Reset form for create mode
      setDepartment("");
      setServices([{ nameAr: "", nameEn: "", price: "", descriptionAr: "", descriptionEn: "" }]);
      setImage(null);
      setOriginalImageUrl(null);
      setImageError("");
      setPhotoRemoved(false);
    }
  }, [editData, currentView, open]);

  // Image handlers
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
      setImageError("");
      setPhotoRemoved(false); // User is adding/changing photo
    }
  };

  const handleRemovePhoto = () => {
    setImage(null);
    setPhotoRemoved(true); // Mark that user explicitly removed photo
  };

  // Handle remove service field (only for create mode)
  const handleRemoveService = (index) => {
    if (currentView === "create" && services.length > 1) {
      const updatedServices = [...services];
      updatedServices.splice(index, 1);
      setServices(updatedServices);
    }
  };

  // Handle service field change
  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...services];
    updatedServices[index][field] = value;
    setServices(updatedServices);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form
      if (!department) {
        toast.error("يرجى اختيار القسم");
        setLoading(false);
        return;
      }

      // Use only the first service in the array
      const service = services[0];

      // Validate service has name and price
      if (!service.nameAr.trim() || !service.price.trim()) {
        toast.error("يرجى إدخال اسم وسعر القطعة");
        setLoading(false);
        return;
      }

      // Validate image for create mode
      if (currentView === "create" && !image) {
        setImageError("الصورة مطلوبة");
        setLoading(false);
        return;
      }

      // Prepare FormData
      const formDataToSend = new FormData();
      formDataToSend.append("name[ar]", service.nameAr);
      formDataToSend.append("name[en]", service.nameEn || service.nameAr);
      formDataToSend.append("description[ar]", service.descriptionAr);
      formDataToSend.append("description[en]", service.descriptionEn || service.descriptionAr);
      formDataToSend.append("price", parseFloat(service.price));
      formDataToSend.append("category", department);

      // Handle photo field
      if (image instanceof File) {
        // User selected a new file (create or edit)
        formDataToSend.append("photo", image);
      } else if (currentView === "edit") {
        // Edit mode: handle existing photo
        if (photoRemoved) {
          // User explicitly removed the photo
          formDataToSend.append("photo", "");
        }
        // If photo wasn't removed and no new file, don't send photo field
        // This will keep the existing photo on the server
      }

      let response;

      if (currentView === "edit" && editData?._id) {
        // Update existing service
        response = await api.patch(`/utility-items/${editData._id}`, formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        // Create new service
        response = await api.post("/utility-items", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      if (response.data.status === "success") {
        onSubmitSuccess();
        toast.success(
          currentView === "edit"
            ? "تم تعديل القطعة بنجاح"
            : "تم إنشاء القطعة بنجاح"
        );
        onClose();
      }
    } catch (error) {
      console.error("Error saving service:", error);
      toast.error(
        error.response?.data?.message ||
          "حدث خطأ أثناء حفظ القطعة، يرجى المحاولة مرة أخرى"
      );
    } finally {
      setLoading(false);
    }
  };

  // Render Create/Edit Service View
  const renderServiceForm = () => {
    const isEdit = currentView === "edit";
    const title = isEdit ? "تعديل قطعة" : "اضافة قطع جديدة";
    const buttonText = isEdit ? "تعديل" : "اضافة";

    return (
      <div className="user-modal">
        <p className="user-modal-p">{title}</p>
        <form onSubmit={handleSubmit}>
          {/* Image Upload Section */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              marginBottom: "16px",
            }}
          >
            <div style={{ position: "relative" }}>
              <label
                htmlFor="image"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <img
                  src={
                    image instanceof File
                      ? URL.createObjectURL(image)
                      : typeof image === "string"
                      ? image
                      : AddPhoto
                  }
                  alt=""
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "1px dashed #ccc",
                  }}
                />
                <span
                  style={{
                    marginTop: "8px",
                    color: "#1976d2",
                    fontSize: "14px",
                  }}
                >
                  {image ? "تغيير الصورة" : "اضافة صورة"}
                </span>
              </label>

              {image && (
                <div
                  onClick={handleRemovePhoto}
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-5px",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: "#f44336",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    fontSize: "18px",
                    fontWeight: "bold",
                  }}
                >
                  ×
                </div>
              )}
            </div>

            <input
              type="file"
              id="image"
              onChange={handleImageChange}
              accept="image/*"
              style={{ display: "none" }}
            />
            {imageError && (
              <p
                style={{
                  color: "#d32f2f",
                  fontSize: "12px",
                  margin: "4px 0 0 0",
                }}
              >
                {imageError}
              </p>
            )}
          </div>

          {/* Department/Category Select */}
          <FormControl
            sx={{
              width: "100%",
            }}
          >
            <InputLabel
              htmlFor="outlined-adornment-password"
              shrink
              sx={{
                background: "white",
                transformOrigin: "right top",
                transform: "translate(14px, -9px) scale(0.75)",
                right: 24,
                left: "auto",
                paddingLeft: "4px",
                paddingRight: "4px",
              }}
            >
              الاقسام
            </InputLabel>
            <Select
              labelId="category-select-label"
              id="category-select"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              displayEmpty={true}
              sx={{ borderRadius: "8px" }}
            >
              <MenuItem value="">
                <em>اختر القسم</em>
              </MenuItem>
              {categories?.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category?.name?.ar}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Service Items */}
          {services.map((service, index) => (
            <div
              key={index}
              className="service-item"
              style={{ marginBottom: "20px", position: "relative" }}
            >
              {/* Arabic Name */}
              <FormControl sx={{ width: "100%" }} variant="outlined">
                <InputLabel
                  htmlFor={`name-ar-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  اسم القطعة بالعربية
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  required
                  id={`name-ar-${index}`}
                  type="text"
                  notched
                  value={service.nameAr}
                  onChange={(e) =>
                    handleServiceChange(index, "nameAr", e.target.value)
                  }
                />
              </FormControl>

              {/* English Name */}
              <FormControl
                sx={{ width: "100%", marginTop: "10px" }}
                variant="outlined"
              >
                <InputLabel
                  htmlFor={`name-en-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  اسم القطعة بالانجليزية
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  id={`name-en-${index}`}
                  type="text"
                  notched
                  value={service.nameEn}
                  onChange={(e) =>
                    handleServiceChange(index, "nameEn", e.target.value)
                  }
                />
              </FormControl>

              {/* Arabic Description */}
              <FormControl
                sx={{ width: "100%", marginTop: "10px" }}
                variant="outlined"
              >
                <InputLabel
                  htmlFor={`description-ar-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  وصف القطعة بالعربية
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  id={`description-ar-${index}`}
                  type="text"
                  multiline
                  rows={3}
                  notched
                  value={service.descriptionAr}
                  onChange={(e) =>
                    handleServiceChange(index, "descriptionAr", e.target.value)
                  }
                />
              </FormControl>

              {/* English Description */}
              <FormControl
                sx={{ width: "100%", marginTop: "10px" }}
                variant="outlined"
              >
                <InputLabel
                  htmlFor={`description-en-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  وصف القطعة بالانجليزية
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  id={`description-en-${index}`}
                  type="text"
                  multiline
                  rows={3}
                  notched
                  value={service.descriptionEn}
                  onChange={(e) =>
                    handleServiceChange(index, "descriptionEn", e.target.value)
                  }
                />
              </FormControl>

              {/* Price */}
              <FormControl
                sx={{ width: "100%", marginTop: "10px" }}
                variant="outlined"
              >
                <InputLabel
                  htmlFor={`price-${index}`}
                  shrink
                  sx={{
                    background: "white",
                    transformOrigin: "right top",
                    transform: "translate(14px, -9px) scale(0.75)",
                    right: 24,
                    left: "auto",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                  }}
                >
                  سعر القطعة
                </InputLabel>
                <OutlinedInput
                  sx={{ borderRadius: "8px" }}
                  required
                  id={`price-${index}`}
                  type="text"
                  notched
                  placeholder="ريال"
                  value={service.price}
                  onChange={(e) =>
                    handleServiceChange(index, "price", e.target.value)
                  }
                />
              </FormControl>

              {/* Remove service button */}
              {currentView === "create" && services.length > 1 && (
                <IconButton
                  aria-label="delete"
                  onClick={() => handleRemoveService(index)}
                  sx={{ position: "absolute", top: 0, left: 0, color: "red" }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          ))}

          <BlueButton
            text={buttonText}
            size="medium"
            variant="fill"
            type="submit"
            disabled={loading}
            icon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          />
        </form>
      </div>
    );
  };

  const renderDefaultView = () => <div></div>;

  const views = {
    create: renderServiceForm,
    edit: renderServiceForm,
  };

  return (
    <SwipeableDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      sx={{ zIndex: "1202" }}
      disableScrollLock
    >
      {(views[currentView] || renderDefaultView)()}
    </SwipeableDrawer>
  );
};

export default SwipeableModals;